/* eslint-disable @typescript-eslint/no-explicit-any */
import { findPdfPathByPolicyCode, findTmdUrlByPolicyCode } from './policy-reference.utils'

function renderActionLink({
  text,
  actionPayload,
  selectedPolicyContents,
  allPolicies,
}: RenderActionLinkArgs): string {
  const providedText = text ?? ''
  if (!actionPayload) return providedText
  const [actionTypeRaw, queryString = ''] = actionPayload.split('?')
  const actionType = actionTypeRaw?.trim().toLowerCase()
  if (!actionType) return providedText
  const params = parseActionParams(queryString)
  const label = resolveActionLinkLabel({ providedText, params, actionType })

  if (actionType === 'policy_wording_link')
    return renderPolicyWordingLink({
      label,
      params,
      selectedPolicyContents,
      allPolicies,
    })

  if (actionType === 'policy_tmd_link')
    return renderPolicyTmdLink({
      label,
      params,
      selectedPolicyContents,
      allPolicies,
    })

  return label
}

function parseActionParams(queryString: string): ActionLinkParams {
  if (!queryString) return {}
  const searchParams = new URLSearchParams(queryString)
  const params: ActionLinkParams = {}
  searchParams.forEach((value, key) => {
    params[key] = value
  })
  return params
}

function renderPolicyWordingLink({
  label,
  params,
  selectedPolicyContents,
  allPolicies,
}: RenderPolicyLinkArgs): string {
  const policyCode = params.policy_code || params.policyCode
  if (!policyCode) return label
  const pdfPath = findPdfPathByPolicyCode(policyCode, allPolicies, selectedPolicyContents)
  if (!pdfPath) return label
  const dataText = params.page || params.page_ref || params.pageRef || label

  return `<a href="#" class="policy-page-link" data-text="${dataText}" data-pdf-path="${pdfPath}" 
  data-policy-code="${policyCode}" title="Open Policy PDF - ${policyCode}"
  target="_blank" rel="noopener noreferrer">${label}</a>`
}

function renderPolicyTmdLink({
  label,
  params,
  selectedPolicyContents,
  allPolicies,
}: RenderPolicyLinkArgs): string {
  const policyCode = resolvePolicyCode({
    label,
    params,
    selectedPolicyContents,
    allPolicies,
  })
  if (!policyCode) return label
  const tmdUrl = findTmdUrlByPolicyCode(policyCode, allPolicies, selectedPolicyContents)
  if (!tmdUrl) return label

  return `<a href="${tmdUrl}" class="policy-tmd-link" data-policy-code="${policyCode}" 
  title="Open TMD - ${policyCode}" target="_blank" rel="noopener noreferrer">${label}</a>`
}

function resolveActionLinkLabel({
  providedText,
  params,
  actionType,
}: ResolveActionLabelArgs): string {
  const trimmed = providedText?.trim()
  if (trimmed) return providedText as string

  if (params) {
    const orderedKeys = ['label', 'page', 'page_ref', 'pageRef', 'policy_code', 'policyCode']
    for (const key of orderedKeys) {
      const value = params[key]
      if (value) return value
    }
  }

  return actionType
}

function resolvePolicyCode({
  label,
  params,
  selectedPolicyContents,
  allPolicies,
}: ResolvePolicyCodeArgs): string | undefined {
  const codeFromParams = params?.policy_code || params?.policyCode
  if (codeFromParams) return codeFromParams
  if (!label) return undefined

  const normalizedLabel = label.toLowerCase()
  if (selectedPolicyContents) {
    for (const policyContent of selectedPolicyContents) {
      const code = policyContent?.policy?.code
      if (code && normalizedLabel.includes(code.toLowerCase())) return code
    }
  }

  if (allPolicies) {
    for (const policy of allPolicies) {
      const code = policy?.code
      if (code && normalizedLabel.includes(code.toLowerCase())) return code
    }
  }

  const regexMatch = label.match(/-\s*([A-Za-z0-9_-]{4,})\s+TMD\b/i)
  if (regexMatch) return regexMatch[1]

  return undefined
}

export { renderActionLink }

interface RenderActionLinkArgs {
  text: string
  actionPayload: string
  selectedPolicyContents?: any[]
  allPolicies?: any[]
}

interface RenderPolicyLinkArgs {
  label: string
  params: ActionLinkParams
  selectedPolicyContents?: any[]
  allPolicies?: any[]
}

interface ActionLinkParams {
  [key: string]: string
}

interface ResolveActionLabelArgs {
  providedText?: string
  params?: ActionLinkParams
  actionType: string
}

interface ResolvePolicyCodeArgs {
  label?: string
  params?: ActionLinkParams
  selectedPolicyContents?: any[]
  allPolicies?: any[]
}
